/**
 * Staff — Account Detail / Edit page.
 *
 * Full view of a single prospect record. Staff can:
 *   • edit any field inline (Name / Phone / Email / survey answers / notes)
 *   • change the pipeline stage via a big segmented picker
 *   • soft-delete the record (admin/manager only)
 *
 * PATCH /api/staff/accounts/:id handles all updates. The optimistic UI
 * on the Kanban does not fully know about survey field changes, so we
 * navigate back to the board after saving so the fresh row picks up on
 * the next `load()`.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Users, Save, Trash2, Loader2, Phone, Mail,
  UserPlus, MessageCircle, FileText, ShoppingCart, Star, Clock, Webhook, RotateCcw, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import useHomePath from '@/hooks/useHomePath';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';

const STAGES = [
  { key: 'new_enquiry',          label: 'New Enquiry',          icon: UserPlus,       accent: '#0f172a' },
  { key: 'spoken_to',            label: 'Spoken To',            icon: MessageCircle,  accent: '#0369a1' },
  { key: 'first_project_quoted', label: 'First Project Quoted', icon: FileText,       accent: '#a16207' },
  { key: 'first_order_placed',   label: 'First Order Placed',   icon: ShoppingCart,   accent: '#166534' },
  { key: 'active_customer',      label: 'Active Customer',      icon: Star,           accent: '#6d28d9' },
];

const SURVEY_FIELDS = [
  { key: 'projects_installed',   label: 'How many resin-bound projects do you currently install?' },
  { key: 'current_supplier',     label: 'Which supplier are you currently using most?' },
  { key: 'what_matters_most',    label: 'What matters most when choosing a resin supplier?' },
  { key: 'current_frustrations', label: 'Biggest frustrations with current supplier?' },
  { key: 'interested_in',        label: 'What would you be interested in?' },
];

const STAGE_LABEL = {
  new_enquiry:          'New Enquiry',
  spoken_to:            'Spoken To',
  first_project_quoted: 'First Project Quoted',
  first_order_placed:   'First Order Placed',
  active_customer:      'Active Customer',
  deleted:              'Deleted',
};

const EVENT_META = {
  created:        { icon: UserPlus,      colour: 'bg-slate-900 text-white', label: 'Created' },
  stage_change:   { icon: Star,          colour: 'bg-amber-500 text-white', label: 'Stage change' },
  assignment:     { icon: Users,         colour: 'bg-indigo-500 text-white', label: 'Assignment' },
  webhook_update: { icon: Webhook,       colour: 'bg-sky-500 text-white',   label: 'Webhook update' },
  deleted:        { icon: XCircle,       colour: 'bg-rose-500 text-white',  label: 'Deleted' },
  restored:       { icon: RotateCcw,     colour: 'bg-emerald-500 text-white', label: 'Restored' },
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const eventDescription = (ev) => {
  if (ev.type === 'created') {
    return ev.source ? `Created from ${ev.source}` : 'Prospect record created';
  }
  if (ev.type === 'stage_change') {
    return `${STAGE_LABEL[ev.from] || ev.from || '—'} → ${STAGE_LABEL[ev.to] || ev.to}`;
  }
  if (ev.type === 'assignment') {
    if (!ev.from && ev.to)  return `Assigned to ${ev.to_name || ev.to}`;
    if (ev.from && !ev.to)  return `Unassigned from ${ev.from_name || ev.from}`;
    return `Reassigned ${ev.from_name || ev.from} → ${ev.to_name || ev.to}`;
  }
  if (ev.type === 'webhook_update') return 'Fields updated from inbound webhook';
  if (ev.type === 'deleted')        return `Deleted from ${STAGE_LABEL[ev.from] || ev.from || '—'}`;
  if (ev.type === 'restored')       return `Restored to ${STAGE_LABEL[ev.to] || ev.to}`;
  return ev.type;
};

export default function AccountDetail() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const canAdmin = ['admin', 'manager'].includes(user?.role);
  const homePath = useHomePath();
  const [account, setAccount] = useState(null);
  const [teammates, setTeammates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/staff/support/staff-members`, { credentials: 'include' });
        if (r.ok) setTeammates(await r.json());
      } catch (_e) { /* silent */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/accounts/${accountId}`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to load');
      setAccount(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    load();
  }, [user, navigate, load]);

  const patch = async (body) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/accounts/${accountId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Save failed');
      setAccount(await res.json());
      toast.success('Saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setAccount((a) => ({ ...a, [k]: v }));

  const saveAll = () =>
    patch({
      name: account.name,
      phone: account.phone,
      email: account.email,
      notes: account.notes,
      projects_installed: account.projects_installed,
      current_supplier: account.current_supplier,
      what_matters_most: account.what_matters_most,
      current_frustrations: account.current_frustrations,
      interested_in: account.interested_in,
    });

  const softDelete = async () => {
    if (!canAdmin) return;
    if (!window.confirm(`Delete ${account?.reference}? It can be restored from the Recently Deleted bin.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/accounts/${accountId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Delete failed');
      toast.success('Enquiry deleted');
      navigate('/staff/accounts');
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading || !account) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white sticky top-0 z-20" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate('/staff/accounts')}
            data-testid="account-detail-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Back to Account Management"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <Users className="w-5 h-5 shrink-0" /> {account.name}
            </h1>
            <p className="text-white/70 text-xs font-mono">{account.reference}</p>
          </div>
          {canAdmin && (
            <Button
              onClick={softDelete}
              variant="outline"
              size="sm"
              className="bg-white/10 border-white/30 text-white hover:bg-rose-500/40 shrink-0"
              data-testid="account-detail-delete"
            >
              <Trash2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Delete</span>
            </Button>
          )}
          <Button
            onClick={() => navigate(homePath)}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
          >
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Stage picker */}
        <Card className="p-4" data-testid="account-stage-picker">
          <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Pipeline stage</div>
          <div className="flex flex-wrap gap-2">
            {STAGES.map((s) => {
              const Icon = s.icon;
              const active = account.status === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => !saving && s.key !== account.status && patch({ status: s.key })}
                  disabled={saving}
                  data-testid={`account-stage-${s.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-full border font-semibold transition ${
                    active
                      ? 'text-white border-transparent'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                  style={active ? { background: s.accent } : {}}
                >
                  <Icon className="w-3.5 h-3.5" /> {s.label}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Assignment */}
        <Card className="p-4" data-testid="account-assignee-card">
          <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Owner</div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={account.assigned_to || ''}
              onChange={(e) => patch({ assigned_to: e.target.value || null })}
              disabled={saving}
              data-testid="account-assignee-select"
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">— Unassigned —</option>
              {teammates.map((t) => (
                <option key={t.user_id} value={t.user_id}>{t.name} ({t.role})</option>
              ))}
            </select>
            {account.assigned_to !== user?.user_id && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => patch({ assigned_to: user?.user_id })}
                disabled={saving}
                data-testid="account-assign-me"
              >
                Assign to me
              </Button>
            )}
          </div>
        </Card>

        {/* Contact */}
        <Card className="p-4">
          <div className="text-[11px] font-bold uppercase text-slate-500 mb-3">Contact</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label htmlFor="acc-name">Name</Label>
              <Input id="acc-name" value={account.name || ''} onChange={(e) => setField('name', e.target.value)} data-testid="account-name" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="acc-phone">Phone</Label>
              <div className="mt-1 flex gap-1">
                <Input id="acc-phone" value={account.phone || ''} onChange={(e) => setField('phone', e.target.value)} data-testid="account-phone" />
                {account.phone && (
                  <a href={`tel:${account.phone}`} className="inline-flex items-center justify-center px-3 border rounded-md hover:bg-slate-50" aria-label="Call">
                    <Phone className="w-4 h-4 text-emerald-700" />
                  </a>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="acc-email">Email</Label>
              <div className="mt-1 flex gap-1">
                <Input id="acc-email" type="email" value={account.email || ''} onChange={(e) => setField('email', e.target.value)} data-testid="account-email" />
                {account.email && (
                  <a href={`mailto:${account.email}`} className="inline-flex items-center justify-center px-3 border rounded-md hover:bg-slate-50" aria-label="Email">
                    <Mail className="w-4 h-4 text-sky-700" />
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            Enquiry date: {account.enquiry_datetime ? new Date(account.enquiry_datetime).toLocaleString('en-GB') : '—'}
            {account.source && <> · Source: <span className="uppercase font-mono">{account.source}</span></>}
          </div>
        </Card>

        {/* Marketing survey */}
        <Card className="p-4">
          <div className="text-[11px] font-bold uppercase text-slate-500 mb-3">Marketing survey</div>
          <div className="space-y-3">
            {SURVEY_FIELDS.map((f) => (
              <div key={f.key}>
                <Label htmlFor={`acc-${f.key}`}>{f.label}</Label>
                <Textarea
                  id={`acc-${f.key}`}
                  rows={2}
                  value={account[f.key] || ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  data-testid={`account-${f.key}`}
                  className="mt-1"
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Notes */}
        <Card className="p-4">
          <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Staff notes</div>
          <Textarea
            rows={4}
            value={account.notes || ''}
            onChange={(e) => setField('notes', e.target.value)}
            placeholder="Call log, next-steps, anything else the team should know…"
            data-testid="account-notes"
          />
        </Card>

        {/* Timeline — chronological log of stage changes, assignments and
             webhook updates so anyone opening the record can see the trail. */}
        <Card className="p-4" data-testid="account-timeline">
          <div className="text-[11px] font-bold uppercase text-slate-500 mb-3 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Timeline
          </div>
          {(!account.history || account.history.length === 0) ? (
            <div className="text-sm text-slate-500 italic py-2">
              No activity logged yet — history starts recording from the next change.
            </div>
          ) : (
            <ol className="relative border-l-2 border-slate-200 ml-3 space-y-4">
              {[...account.history].reverse().map((ev) => {
                const meta = EVENT_META[ev.type] || { icon: Clock, colour: 'bg-slate-500 text-white', label: ev.type };
                const Icon = meta.icon;
                return (
                  <li key={ev.id} className="ml-6" data-testid={`timeline-${ev.type}`}>
                    <span className={`absolute -left-[13px] w-6 h-6 rounded-full flex items-center justify-center shadow ${meta.colour}`}>
                      <Icon className="w-3 h-3" />
                    </span>
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-sm font-semibold text-slate-900">
                        {eventDescription(ev)}
                      </span>
                      <span className="text-[11px] text-slate-500">
                        {fmtWhen(ev.at)}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      by <span className="font-semibold">{ev.by_name || ev.by || 'System'}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </Card>

        <div className="flex justify-end sticky bottom-4">
          <Button onClick={saveAll} disabled={saving} data-testid="account-save" className="bg-slate-900 hover:bg-slate-800 shadow-lg">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> Save changes</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
